// Privacy Policy of superluxury.io — verbatim from the approved iubenda document (latest update: April 22, 2026).
import type { LegalDocument, LegalSection } from './legal';
import { definitionsSection, legalInformationSection, ownerDetails } from './legalShared';

const service = (heading: string, lines: string[]): LegalSection => ({ heading, paragraphs: lines, minor: true });

export const privacyPolicy: LegalDocument = {
  slug: 'privacy-policy',
  title: 'Privacy Policy',
  updated: '22 April 2026',
  intro:
    'Welcome to the privacy policy of superluxury.io. This policy will help you understand what data we collect, why we collect it, and what your rights are in relation to it.',
  sections: [
    {
      heading: 'Owner and Data Controller',
      paragraphs: ownerDetails,
      after: ['Owner contact email: hey@superluxury.io'],
      compact: true,
    },
    {
      heading: 'Type of Data we collect',
      paragraphs: ['Among the types of Personal Data that this Website collects, by itself or through third parties, there are:'],
      list: [
        'Trackers',
        'Usage Data',
        'number of Users',
        'session statistics',
        'first name',
        'last name',
        'phone number',
        'company name',
        'country',
        'email address',
        'various types of Data',
        'website',
        'Data communicated while using the service',
        'answers to questions',
        'user content',
        'contents of the email or message',
        'profile picture',
        'date of the message',
        'time the message was sent',
        'sender of the message',
      ],
      after: [
        'Complete details on each type of Personal Data collected are provided in the dedicated sections of this privacy policy or by specific explanation texts displayed prior to the Data collection.',
        'Personal Data may be freely provided by the User, or, in case of Usage Data, collected automatically when using this Website.',
        'Unless specified otherwise, all Data requested by this Website is mandatory and failure to provide this Data may make it impossible for this Website to provide its services. In cases where this Website specifically states that some Data is not mandatory, Users are free not to communicate this Data without consequences to the availability or the functioning of the Service.',
        'Users who are uncertain about which Personal Data is mandatory are welcome to contact the Owner.',
        'Any use of Cookies – or of other tracking tools — by this Website or by the owners of third-party services used by this Website serves the purpose of providing the Service required by the User, in addition to any other purposes described in the present document and in the Cookie Policy.',
        'Users are responsible for any third-party Personal Data obtained, published or shared through this Website.',
      ],
    },
    {
      heading: 'Mode and place of processing the Data',
      paragraphs: [],
      subsections: [
        {
          heading: 'Methods of processing',
          paragraphs: [
            'The Owner takes appropriate security measures to prevent unauthorized access, disclosure, modification, or unauthorized destruction of the Data.',
            'The Data processing is carried out using computers and/or IT enabled tools, following organizational procedures and modes strictly related to the purposes indicated. In addition to the Owner, in some cases, the Data may be accessible to certain types of persons in charge, involved with the operation of this Website (administration, sales, marketing, legal, system administration) or external parties (such as third-party technical service providers, mail carriers, hosting providers, IT companies, communications agencies) appointed, if necessary, as Data Processors by the Owner. The updated list of these parties may be requested from the Owner at any time.',
          ],
        },
        {
          heading: 'Place',
          paragraphs: [
            "The Data is processed at the Owner's operating offices and in any other places where the parties involved in the processing are located.",
            "Depending on the User's location, data transfers may involve transferring the User's Data to a country other than their own. To find out more about the place of processing of such transferred Data, Users can check the section containing details about the processing of Personal Data.",
          ],
        },
        {
          heading: 'Retention time',
          paragraphs: [
            'Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users’ consent.',
          ],
        },
      ],
    },
    {
      heading: 'The purposes of processing',
      paragraphs: [
        'The Data concerning the User is collected to allow the Owner to provide its Service, comply with its legal obligations, respond to enforcement requests, protect its rights and interests (or those of its Users or third parties), detect any malicious or fraudulent activity, as well as the following:',
      ],
      list: [
        'Analytics',
        'Displaying content from external platforms',
        'Contacting the User',
        'Interaction with live chat platforms',
        'Platform services and hosting',
        'Managing support and contact requests',
        'Advertising',
        'Traffic optimization and distribution',
      ],
    },
    {
      heading: 'Detailed information on the processing of Personal Data',
      paragraphs: [],
      subsections: [
        {
          heading: 'Advertising',
          paragraphs: [
            'This type of service allows User Data to be utilized for advertising communication purposes. These communications are displayed in the form of banners and other advertisements on this Website, possibly based on User interests.',
            'This does not mean that all Personal Data are used for this purpose. Information and conditions of use are shown below.',
            'Some of the services listed below may use Trackers to identify Users or they may use the behavioral retargeting technique, i.e. displaying ads tailored to the User’s interests and behavior, including those detected outside this Website.',
            'For more information, please check the privacy policies of the relevant services.',
            'Services of this kind usually allow Users to opt out of such tracking. Users may learn how to opt out of interest-based advertising more generally by visiting the relevant opt-out section in this document.',
          ],
        },
        service('WhatsApp Business direct marketing', [
          'Company: Meta Platforms, Inc.',
          'Place of processing: United States',
          'Personal Data processed: answers to questions +10',
        ]),
        {
          heading: 'Analytics',
          paragraphs: [
            'The services contained in this section enable the Owner to monitor and analyze web traffic and can be used to keep track of User behavior.',
          ],
        },
        service('Google Analytics 4', [
          'Company: Google Ireland Limited',
          'Place of processing: Ireland',
          'Personal Data processed: number of Users +3',
        ]),
        { heading: 'Contacting the User', paragraphs: [] },
        service('Contact form', ['Personal Data processed: company name +7']),
        service('Mailing list or newsletter', ['Personal Data processed: email address +4']),
        {
          heading: 'Displaying content from external platforms',
          paragraphs: [
            'This type of service allows you to view content hosted on external platforms directly from the pages of this Website and interact with them. Such services are often referred to as widgets, which are small elements placed on a website or app. They provide specific information or perform a particular function and often allow for user interaction.',
            'This type of service might still collect web traffic data for the pages where the service is installed, even when Users do not use it.',
          ],
        },
        service('Google Fonts', [
          'Company: Google LLC +1',
          'Place of processing: United States +1',
          'Personal Data processed: Trackers +1',
        ]),
        service('Google Maps widget', [
          'Company: Google Ireland Limited',
          'Place of processing: Ireland',
          'Personal Data processed: Trackers +1',
        ]),
        {
          heading: 'Interaction with live chat platforms',
          paragraphs: [
            'This type of service allows Users to interact with third-party live chat platforms directly from the pages of this Website, in order to contact and be contacted by this Website‘s support service.',
            'If one of these services is installed, it may collect browsing and Usage Data in the pages where it is installed, even if the Users do not actively use the service. Moreover, live chat conversations may be logged.',
          ],
        },
        service('WhatsApp Business Chat widget', [
          'Company: Meta Platforms Ireland Limited',
          'Place of processing: Ireland',
          'Personal Data processed: answers to questions +10',
        ]),
        {
          heading: 'Managing support and contact requests',
          paragraphs: [
            'This type of service allows this Website to manage support and contact requests received via email or by other means, such as the contact form.',
            'The Personal Data processed depend on the information provided by the User in the messages and the means used for communication (e.g. email address).',
          ],
        },
        service('WhatsApp Business customer support', [
          'Company: Meta Platforms, Inc.',
          'Place of processing: United States',
          'Personal Data processed: Data communicated while using the service +1',
        ]),
        {
          heading: 'Platform services and hosting',
          paragraphs: [
            'These services have the purpose of hosting and running key components of this Website, therefore allowing the provision of this Website from within a unified platform. Such platforms provide a wide range of tools to the Owner – e.g. analytics, user registration, commenting, database management, e-commerce, payment processing – that imply the collection and handling of Personal Data.',
            'Some of these services work through geographically distributed servers, making it difficult to determine the actual location where the Personal Data are stored.',
          ],
        },
        service('Webflow', [
          'Company: Webflow, Inc.',
          'Place of processing: United States',
          'Personal Data processed: Trackers +2',
        ]),
        {
          heading: 'Traffic optimization and distribution',
          paragraphs: [
            'This type of service allows this Website to distribute their content using servers located across different countries and to optimize their performance.',
            "Which Personal Data are processed depends on the characteristics and the way these services are implemented. Their function is to filter communications between this Website and the User's browser.",
            'Considering the widespread distribution of this system, it is difficult to determine the locations to which the contents that may contain Personal Information of the User are transferred.',
          ],
        },
        service('Bunny CDN', [
          'Company: BunnyWay d.o.o.',
          'Place of processing: Slovenia',
          'Personal Data processed: Usage Data',
        ]),
      ],
    },
    {
      heading: 'Cookie Policy',
      paragraphs: ['This Website uses Trackers. To learn more, Users may consult the Cookie Policy.'],
    },
    {
      heading: 'Further Information for Users in the European Union',
      paragraphs: [
        'This section applies to all Users in the European Union, according to the General Data Protection Regulation (the “GDPR”), and, for such Users, supersedes any other possibly divergent or conflicting information contained in the privacy policy. Further details regarding the categories of Data processed, the purposes of processing, the categories of recipients of the Personal Data, if any, and further information about Personal Data can be found in the section titled “Detailed information on the processing of Personal Data” within this document.',
      ],
      subsections: [
        {
          heading: 'Legal basis of processing',
          paragraphs: ['The Owner may process Personal Data relating to Users if one of the following applies:'],
          list: [
            'Users have given their consent for one or more specific purposes.',
            'provision of Data is necessary for the performance of an agreement with the User and/or for any pre-contractual obligations thereof;',
            'processing is necessary for compliance with a legal obligation to which the Owner is subject;',
            'processing is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Owner;',
            'processing is necessary for the purposes of the legitimate interests pursued by the Owner or by a third party.',
          ],
          after: [
            'In any case, the Owner will gladly help to clarify the specific legal basis that applies to the processing, and in particular whether the provision of Personal Data is a statutory or contractual requirement, or a requirement necessary to enter into a contract.',
          ],
        },
        {
          heading: 'Further information about retention time',
          paragraphs: [
            'Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users’ consent.',
            'Therefore:',
          ],
          list: [
            'Personal Data collected for purposes related to the performance of a contract between the Owner and the User shall be retained until such contract has been fully performed.',
            'Personal Data collected for the purposes of the Owner’s legitimate interests shall be retained as long as needed to fulfill such purposes. Users may find specific information regarding the legitimate interests pursued by the Owner within the relevant sections of this document or by contacting the Owner.',
          ],
          after: [
            'The Owner may be allowed to retain Personal Data for a longer period whenever the User has given consent to such processing, as long as such consent is not withdrawn. Furthermore, the Owner may be obliged to retain Personal Data for a longer period whenever required to fulfil a legal obligation or upon order of an authority.',
            'Once the retention period expires, Personal Data shall be deleted. Therefore, the right of access, the right to erasure, the right to rectification and the right to data portability cannot be enforced after expiration of the retention period.',
          ],
        },
        {
          heading: 'The rights of Users based on the General Data Protection Regulation (GDPR)',
          paragraphs: [
            'Users may exercise certain rights regarding their Data processed by the Owner.',
            'In particular, Users have the right to do the following, to the extent permitted by law:',
          ],
          list: [
            'Withdraw their consent at any time. Users have the right to withdraw consent where they have previously given their consent to the processing of their Personal Data.',
            'Object to processing of their Data. Users have the right to object to the processing of their Data if the processing is carried out on a legal basis other than consent.',
            'Access their Data. Users have the right to learn if Data is being processed by the Owner, obtain disclosure regarding certain aspects of the processing and obtain a copy of the Data undergoing processing.',
            'Verify and seek rectification. Users have the right to verify the accuracy of their Data and ask for it to be updated or corrected.',
            'Restrict the processing of their Data. Users have the right to restrict the processing of their Data. In this case, the Owner will not process their Data for any purpose other than storing it.',
            'Have their Personal Data deleted or otherwise removed. Users have the right to obtain the erasure of their Data from the Owner.',
            'Receive their Data and have it transferred to another controller. Users have the right to receive their Data in a structured, commonly used and machine readable format and, if technically feasible, to have it transmitted to another controller without any hindrance.',
            'Lodge a complaint. Users have the right to bring a claim before their competent data protection authority.',
          ],
          after: [
            'Users are also entitled to learn about the legal basis for Data transfers abroad including to any international organization governed by public international law or set up by two or more countries, such as the UN, and about the security measures taken by the Owner to safeguard their Data.',
          ],
        },
        {
          heading: 'Details about the right to object to processing',
          paragraphs: [
            'Where Personal Data is processed for a public interest, in the exercise of an official authority vested in the Owner or for the purposes of the legitimate interests pursued by the Owner, Users may object to such processing by providing a ground related to their particular situation to justify the objection.',
            'Users must know that, however, should their Personal Data be processed for direct marketing purposes, they can object to that processing at any time, free of charge and without providing any justification. Where the User objects to processing for direct marketing purposes, the Personal Data will no longer be processed for such purposes. To learn whether the Owner is processing Personal Data for direct marketing purposes, Users may refer to the relevant sections of this document.',
          ],
        },
        {
          heading: 'How to exercise these rights',
          paragraphs: [
            'Any requests to exercise User rights can be directed to the Owner through the contact details provided in this document. Such requests are free of charge and will be answered by the Owner as early as possible and always within one month, providing Users with the information required by law. Any rectification or erasure of Personal Data or restriction of processing will be communicated by the Owner to each recipient, if any, to whom the Personal Data has been disclosed unless this proves impossible or involves disproportionate effort. At the Users’ request, the Owner will inform them about those recipients.',
          ],
        },
      ],
    },
    {
      heading: 'Additional information about Data collection and processing',
      paragraphs: [],
      subsections: [
        {
          heading: 'Legal action',
          paragraphs: [
            "The User's Personal Data may be used for legal purposes by the Owner in Court or in the stages leading to possible legal action arising from improper use of this Website or the related Services.",
            'The User declares to be aware that the Owner may be required to reveal personal data upon request of public authorities.',
          ],
        },
        {
          heading: "Additional information about User's Personal Data",
          paragraphs: [
            'In addition to the information contained in this privacy policy, this Website may provide the User with additional and contextual information concerning particular Services or the collection and processing of Personal Data upon request.',
          ],
        },
        {
          heading: 'System logs and maintenance',
          paragraphs: [
            'For operation and maintenance purposes, this Website and any third-party services may collect files that record interaction with this Website (System logs) or use other Personal Data (such as the IP Address) for this purpose.',
          ],
        },
        {
          heading: 'Information not contained in this policy',
          paragraphs: [
            'More details concerning the collection or processing of Personal Data may be requested from the Owner at any time. Please see the contact information at the beginning of this document.',
          ],
        },
        {
          heading: 'Changes to this privacy policy',
          paragraphs: [
            'The Owner reserves the right to make changes to this privacy policy at any time by notifying its Users on this page and possibly within this Website and/or - as far as technically and legally feasible - sending a notice to Users via any contact information available to the Owner. It is strongly recommended to check this page often, referring to the date of the last modification listed at the bottom.',
            'Should the changes affect processing activities performed on the basis of the User’s consent, the Owner shall collect new consent from the User, where required.',
          ],
        },
      ],
    },
    definitionsSection,
    legalInformationSection,
    {
      heading: 'How can we help?',
      paragraphs: [],
      subsections: [
        {
          heading: 'Your data',
          paragraphs: [],
          list: [
            'Ask us to know and access the information we hold on you',
            'Ask us to correct information we hold on you',
            'Ask us to be forgotten (delete the information we hold on you)',
            'Ask to port your data to another service',
          ],
        },
        {
          heading: 'In case of issues',
          paragraphs: [
            'While we strive to create a positive user experience, we understand that issues may occasionally arise between us and our users. If this is the case, please feel free to contact us at hey@superluxury.io.',
          ],
        },
      ],
    },
  ],
};
